/** Item lifecycle statuses (B4) */
export const ITEM_STATUS = {
  ACTIVE: "ACTIVE",
  HIDDEN: "HIDDEN",
  RETIRED: "RETIRED",
};

export const ITEM_STATUS_OPTIONS = [
  { value: "ALL", label: "All Statuses" },
  { value: "ACTIVE", label: "Active" },
  { value: "HIDDEN", label: "Hidden" },
  { value: "RETIRED", label: "Retired" },
];

export const ITEM_STATUS_FORM_OPTIONS = [
  { value: "ACTIVE", label: "Active — sells & renews" },
  { value: "HIDDEN", label: "Hidden — stops new sales" },
  { value: "RETIRED", label: "Retired — stops renewals" },
];

export const KIND_OPTIONS = [
  { value: "ALL", label: "All Kinds" },
  { value: "LIMIT", label: "Limit (capacity / seats)" },
  { value: "ACCESS", label: "Access (module / feature)" },
];

export const ADDON_SOURCE_OPTIONS = [
  { value: "ALL", label: "All Sources" },
  { value: "PURCHASE", label: "Purchase" },
  { value: "GRANT", label: "Manual Grant" },
];

export const ADDON_STATUS_OPTIONS = [
  { value: "ALL", label: "All Statuses" },
  { value: "ACTIVE", label: "Active" },
  { value: "REVOKED", label: "Revoked" },
  { value: "EXPIRED", label: "Expired" },
  { value: "PENDING", label: "Pending" },
];

/** Only these four base plans appear in eligibility checkboxes. */
export const BASE_PLAN_NAMES = [
  "Simple Start",
  "Standard",
  "Premium",
  "Enterprise",
];

const BASE_PLAN_ALIASES = {
  "simple start": "Simple Start",
  simplestart: "Simple Start",
  standard: "Standard",
  premium: "Premium",
  enterprise: "Enterprise",
  "enterprise plan": "Enterprise",
};

/**
 * Keep only Simple Start, Standard, Premium, and Enterprise — in that order.
 */
export function filterBasePlans(plans = []) {
  const byKey = new Map();
  (plans || []).forEach((p) => {
    const raw = String(p.name || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");
    const compact = raw.replace(/\s/g, "");
    const canonical =
      BASE_PLAN_ALIASES[raw] ||
      BASE_PLAN_ALIASES[compact] ||
      BASE_PLAN_NAMES.find((n) => n.toLowerCase() === raw);
    if (!canonical || byKey.has(canonical)) return;
    byKey.set(canonical, { ...p, name: canonical });
  });

  return BASE_PLAN_NAMES.map((name) => byKey.get(name)).filter(Boolean);
}

/**
 * Item code for a capability rule: prefer soldBy pack code, else the rule key.
 */
export function itemCodeFromRule(rule) {
  if (!rule) return "";
  const soldBy = Array.isArray(rule.soldBy) ? rule.soldBy : [];
  if (soldBy[0]) return String(soldBy[0]).toUpperCase();
  return String(rule.rule || "").toUpperCase();
}

/**
 * Map meta endpoint options into AppSelectField items.
 * Accepts arrays of strings or { value/label/code/name } objects.
 */
export function metaOptions(list, fallback = []) {
  const source = Array.isArray(list) && list.length ? list : fallback;
  return source.map((entry) => {
    if (typeof entry === "string") {
      return { value: entry, label: formatEnumLabel(entry) };
    }
    const value = entry.value ?? entry.code ?? entry.id ?? entry.key;
    const label = entry.label ?? entry.name ?? formatEnumLabel(String(value));
    return { value, label };
  });
}

export function formatEnumLabel(value = "") {
  return String(value)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function slugifyCode(name = "") {
  return String(name)
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 48);
}

export function categoryFromRule(ruleCode = "", label = "") {
  const code = String(ruleCode || "").toUpperCase();
  if (code.startsWith("HR_")) return "HR";
  if (code.startsWith("ACCOUNTING_")) {
    if (code.includes("INVOICE")) return "Invoice Capacity";
    return "Accounting";
  }
  if (code.startsWith("ORG_")) return "Operations";
  if (code.startsWith("TAX_")) return "Finance";
  if (code.startsWith("EXTENSION_") || code.startsWith("INTEGRATION_")) {
    return "Integrations";
  }
  if (code.includes("CURRENCY") || code.includes("PAYMENT") || code.includes("REPORT")) {
    return "Finance";
  }
  if (label) return label;
  return "General";
}

/**
 * Resolve which base plans can buy an item.
 * Empty planIds + empty excludePlanIds ⇒ all plans eligible.
 */
export function resolveEligiblePlans(item, allPlans = []) {
  if (Array.isArray(item?.eligiblePlans) && item.eligiblePlans.length) {
    return item.eligiblePlans.map((p) =>
      typeof p === "string" ? { id: p, name: p } : { id: p.id || p._id, name: p.name }
    );
  }
  if (Array.isArray(item?.plans) && item.plans.length) {
    return item.plans
      .filter((p) => p.eligible !== false && p.excluded !== true)
      .map((p) => ({ id: p.id || p._id, name: p.name }));
  }

  const planIds = (item?.eligibility?.planIds || []).map(String).filter(Boolean);
  const excluded = (item?.eligibility?.excludePlanIds || item?.excludePlanIds || [])
    .map(String)
    .filter(Boolean);

  // Allow-list wins when present
  if (planIds.length) {
    if (!allPlans.length) {
      return planIds.map((id) => ({ id, name: id }));
    }
    return allPlans
      .filter((p) => planIds.includes(String(p.id || p._id)))
      .map((p) => ({ id: p.id || p._id, name: p.name }));
  }

  // No exclusions ⇒ every plan can purchase
  if (!excluded.length) {
    return [{ id: "*", name: "All plans" }];
  }

  if (!allPlans.length) {
    return [];
  }

  const excludedSet = new Set(excluded);
  return allPlans
    .filter((p) => !excludedSet.has(String(p.id || p._id)))
    .map((p) => ({ id: p.id || p._id, name: p.name }));
}

/**
 * Monthly catalogue price from either legacy monthlyAmount or pricing.monthly.amount.
 */
export function getMonthlyAmount(item) {
  if (!item) return 0;
  if (item.monthlyAmount != null && item.monthlyAmount !== "") {
    return Number(item.monthlyAmount) || 0;
  }
  if (item.price != null && item.price !== "") return Number(item.price) || 0;
  const monthly = item.pricing?.monthly?.amount;
  if (monthly != null && monthly !== "") return Number(monthly) || 0;
  return 0;
}

const PRICING_INTERVALS = [
  { key: "monthly", label: "Monthly" },
  { key: "quarterly", label: "Quarterly" },
  { key: "biannually", label: "Biannually" },
  { key: "annually", label: "Annually" },
];

/**
 * Normalize pricing object (or derive from monthly) for display tables.
 */
export function getPricingRows(item, discountRate = 0.1) {
  const pricing = item?.pricing;
  if (pricing && typeof pricing === "object") {
    return PRICING_INTERVALS.map(({ key, label }) => {
      const slot = pricing[key] || {};
      return {
        key,
        label,
        amount: Number(slot.amount) || 0,
        isActive: slot.isActive !== false,
      };
    });
  }

  const monthly = getMonthlyAmount(item);
  const d = Number.isFinite(Number(discountRate)) ? Number(discountRate) : 0.1;
  return [
    { key: "monthly", label: "Monthly", amount: Math.round(monthly), isActive: true },
    { key: "quarterly", label: "Quarterly", amount: Math.round(monthly * 3), isActive: true },
    {
      key: "biannually",
      label: "Biannually",
      amount: Math.round(monthly * 6 * (1 - d / 2)),
      isActive: true,
    },
    {
      key: "annually",
      label: "Annually",
      amount: Math.round(monthly * 12 * (1 - d)),
      isActive: true,
    },
  ];
}

export function formatDateTime(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("en-NG", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return String(value);
  }
}

export function normalizePlans(res) {
  const payload = res?.data?.data ?? res?.data ?? [];
  const list = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.docs)
      ? payload.docs
      : Array.isArray(payload?.plans)
        ? payload.plans
        : [];

  const normalized = list
    .map((p) => ({
      id: String(p.id || p._id),
      name: p.name || p.planName || "Plan",
      rank: p.rank ?? p.order ?? 0,
    }))
    .sort((a, b) => a.rank - b.rank);

  return filterBasePlans(normalized);
}

/**
 * Plans already granting a capability rule — used when attaching rules to new items.
 */
export function plansGrantingRule(rule) {
  if (!rule?.plans) return [];
  return (rule.plans || [])
    .filter((p) => p.granted)
    .map((p) => ({ id: p.id, name: p.name, value: p.value, unlimited: p.unlimited }));
}

export function computeCatalogStats(items = []) {
  const list = Array.isArray(items) ? items : [];
  const active = list.filter((i) => String(i.status).toUpperCase() === "ACTIVE").length;
  const hidden = list.filter((i) => String(i.status).toUpperCase() === "HIDDEN").length;
  const retired = list.filter((i) => String(i.status).toUpperCase() === "RETIRED").length;
  const purchases = list.reduce(
    (sum, i) => sum + (Number(i.purchaseCount ?? i.purchases ?? 0) || 0),
    0
  );

  return {
    total: list.length,
    active,
    hidden,
    retired,
    purchases,
    comingSoon: hidden,
    disabled: retired,
    mrr: 0,
    outstanding: 0,
    activeBusinesses: 0,
  };
}

export function formatCategoryLabel(item, rulesByCode = {}) {
  if (item?.category) return item.category;
  const firstRule = Array.isArray(item?.rules) ? item.rules[0] : item?.rule;
  const ruleMeta = firstRule ? rulesByCode[firstRule] : null;
  return categoryFromRule(firstRule, ruleMeta?.label);
}

/** True when kind / rules / unitValue must stay locked. */
export function isItemLocked(item) {
  return Number(item?.purchaseCount ?? item?.purchases ?? 0) > 0;
}

export { marketplaceItemPath, marketplaceItemEditPath } from "../../constants/pathnames";
